import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Loader2 } from "lucide-react";
import { Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { toast } from "sonner";

const SUPPLIER_TYPES = ["Local", "Importer", "Wholesale", "Food Service", "General"];

const EMPTY = {
  code: "",
  name: "",
  type: "Local",
  status: "Active",
  contact: "",
  phone: "",
  mobile: "",
  email: "",
  fax: "",
  address: "",
  country: "UAE",
  credit_period: 30,
  credit: 0,
  balance: 0,
  opening_date: "2026-01-01",
  account_type: "Credit",
  tax_number: "",
  trade_licence_expiry: "",
  halal_certificate_expiry: "",
};

function supplierToForm(s) {
  if (!s) return { ...EMPTY };
  return {
    code: s.code || "",
    name: s.name || "",
    type: s.type || "Local",
    status: s.status || "Active",
    contact: s.contact || "",
    phone: s.phone || "",
    mobile: s.mobile || s.phone || "",
    email: s.email || "",
    fax: s.fax || "",
    address: s.address || "",
    country: s.country || "UAE",
    credit_period: s.credit_period ?? 30,
    credit: s.credit ?? 0,
    balance: s.balance ?? 0,
    opening_date: s.opening_date || "2026-01-01",
    account_type: s.account_type || "Credit",
    tax_number: s.tax_number || "",
    trade_licence_expiry: s.trade_licence_expiry || "",
    halal_certificate_expiry: s.halal_certificate_expiry || "",
  };
}

function formToPayload(form) {
  return {
    code: form.code,
    name: form.name,
    subtype: form.type,
    status: form.status,
    contact_person: form.contact || null,
    phone: form.phone || null,
    mobile: form.mobile || null,
    email: form.email || null,
    fax: form.fax || null,
    address: form.address || null,
    country: form.country,
    credit_period: Number(form.credit_period) || 0,
    credit_limit: Number(form.credit) || 0,
    opening_balance: Number(form.balance) || 0,
    balance: Number(form.balance) || 0,
    opening_date: form.opening_date || null,
    account_type: form.account_type,
    tax_number: form.tax_number || null,
    trade_licence_expiry: form.trade_licence_expiry || null,
    halal_certificate_expiry: form.halal_certificate_expiry || null,
  };
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

export default function SupplierMaster() {
  const { user } = useAuth();
  const { format, step: moneyStep, code: currencyCode } = useCurrency();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ["suppliers", branchId],
    queryFn: async () => {
      const { data } = await api.get("/suppliers", { params: { branch_id: branchId } });
      return data.suppliers;
    },
    enabled: Boolean(branchId),
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return suppliers;
    return suppliers.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.code?.toLowerCase().includes(q) ||
        s.phone?.toLowerCase().includes(q) ||
        s.mobile?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.contact?.toLowerCase().includes(q) ||
        s.tax_number?.toLowerCase().includes(q),
    );
  }, [suppliers, search]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload(form);
      if (editingId) {
        return api.put(`/suppliers/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/suppliers", payload, { params: { branch_id: branchId } });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["suppliers", branchId] });
      toast.success(res.data.message);
      setEditingId(null);
      setForm(EMPTY);
      setView("form");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to save supplier");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/suppliers/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers", branchId] });
      toast.success("Supplier deleted");
    },
    onError: () => toast.error("Failed to delete supplier"),
  });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function openNew() {
    setEditingId(null);
    const next = suppliers.length + 1;
    setForm({ ...EMPTY, code: `SUP-${String(next).padStart(3, "0")}` });
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm(supplierToForm(row));
    setView("form");
  }

  function handleSave() {
    if (!form.code || !form.name) {
      toast.error("Code and name are required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="Supplier Master"
      subtitle={`${suppliers.length} suppliers — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      saveDisabled={!form.code || !form.name}
      onAddNew={openNew}
      testIdPrefix="sup"
      formTitle={editingId ? form.name || "Edit Supplier" : "Add New Supplier"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <>
          <DataToolbar
            searchValue={search}
            onSearch={setSearch}
            testIdPrefix="sup"
          />
          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <DataTable
              testIdPrefix="sup"
              emptyText="No suppliers yet"
              onRowClick={openEdit}
              columns={[
                { key: "code", label: "Code", mono: true, bold: true },
                { key: "name", label: "Name", bold: true },
                { key: "type", label: "Type" },
                { key: "phone", label: "Phone", render: (v, row) => v || row.mobile || "—" },
                { key: "email", label: "Email", render: (v) => v || "—" },
                { key: "tax_number", label: currencyCode === "OMR" ? "VATIN" : "TRN", mono: true, render: (v) => v || "—" },
                {
                  key: "trade_licence_expiry",
                  label: "Trade licence",
                  render: (v) => v || "—",
                },
                {
                  key: "halal_certificate_expiry",
                  label: "Halal cert",
                  render: (v) => v || "—",
                },
                {
                  key: "balance",
                  label: "Balance",
                  align: "right",
                  render: (v) => format(v || 0),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (v) => <Pill tone={v === "Active" ? "green" : v === "Blacklisted" ? "red" : "default"}>{v}</Pill>,
                },
              ]}
              rows={filtered}
              rowActions={(row) => [
                { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
                {
                  title: "Delete",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  onClick: () => {
                    if (window.confirm(`Delete supplier ${row.name}?`)) {
                      deleteMutation.mutate(row.id);
                    }
                  },
                },
              ]}
            />
          )}
        </>
      }
    >
      <Tabs key={editingId || "new"} defaultValue="basic">
        <TabsList className="bg-slate-100 h-9">
          <TabsTrigger value="basic" className="text-[12.5px] h-7 px-3" data-testid="tab-basic">
            Basic
          </TabsTrigger>
          <TabsTrigger value="contact" className="text-[12.5px] h-7 px-3" data-testid="tab-contact">
            Contact
          </TabsTrigger>
          <TabsTrigger value="finance" className="text-[12.5px] h-7 px-3" data-testid="tab-finance">
            Financial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Code *">
              <Input
                value={form.code}
                onChange={(e) => setField("code", e.target.value)}
                className="h-9 font-mono"
                disabled={Boolean(editingId)}
                autoFocus={!editingId}
              />
            </Field>
            <Field label="Name *">
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className="h-9"
                autoFocus={Boolean(editingId)}
              />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setField("type", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUPPLIER_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Blacklisted">Blacklisted</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Trade licence expiry">
              <Input
                type="date"
                value={form.trade_licence_expiry}
                onChange={(e) => setField("trade_licence_expiry", e.target.value)}
                className="h-9"
              />
            </Field>
            <Field label="Halal certificate expiry">
              <Input
                type="date"
                value={form.halal_certificate_expiry}
                onChange={(e) => setField("halal_certificate_expiry", e.target.value)}
                className="h-9"
              />
            </Field>
          </div>
        </TabsContent>

        <TabsContent value="contact" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Address" className="md:col-span-2">
              <Input value={form.address} onChange={(e) => setField("address", e.target.value)} className="h-9" />
            </Field>
            <Field label="Country">
              <Select value={form.country} onValueChange={(v) => setField("country", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UAE">UAE</SelectItem>
                  <SelectItem value="KSA">Saudi Arabia</SelectItem>
                  <SelectItem value="OMN">Oman</SelectItem>
                  <SelectItem value="QAT">Qatar</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Contact Person">
              <Input value={form.contact} onChange={(e) => setField("contact", e.target.value)} className="h-9" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="h-9" />
            </Field>
            <Field label="Mobile">
              <Input value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} className="h-9" />
            </Field>
            <Field label="Fax">
              <Input value={form.fax} onChange={(e) => setField("fax", e.target.value)} placeholder="—" className="h-9" />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setField("email", e.target.value)} className="h-9" />
            </Field>
          </div>
        </TabsContent>

        <TabsContent value="finance" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Credit Period (days)">
              <Input
                type="number"
                value={form.credit_period}
                onChange={(e) => setField("credit_period", e.target.value)}
                className="h-9 text-right"
              />
            </Field>
            <Field label={`Credit Limit (${currencyCode})`}>
              <Input
                type="number"
                step={moneyStep}
                value={form.credit}
                onChange={(e) => setField("credit", e.target.value)}
                className="h-9 font-mono text-right"
              />
            </Field>
            <Field label={`Opening Balance (${currencyCode})`}>
              <Input
                type="number"
                step={moneyStep}
                value={form.balance}
                onChange={(e) => setField("balance", e.target.value)}
                className="h-9 font-mono text-right"
              />
            </Field>
            <Field label="Opening Date">
              <Input
                type="date"
                value={form.opening_date}
                onChange={(e) => setField("opening_date", e.target.value)}
                className="h-9"
              />
            </Field>
            <Field label="Account Type">
              <Select value={form.account_type} onValueChange={(v) => setField("account_type", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Debit">Debit</SelectItem>
                  <SelectItem value="Credit">Credit</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={currencyCode === "OMR" ? "VATIN Number" : "VAT/TRN Number"}>
              <Input
                value={form.tax_number}
                onChange={(e) => setField("tax_number", String(e.target.value || "").replace(/\D/g, "").slice(0, 15))}
                placeholder="100xxxxxxxx00003"
                maxLength={15}
                inputMode="numeric"
                className="h-9 font-mono"
              />
            </Field>
          </div>
        </TabsContent>
      </Tabs>
    </MasterCrudShell>
  );
}
